import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import api from '@/db/apiClient';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { confirmPasswordReset } from 'firebase/auth';
import { AlertCircleIcon } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthViewWrapper, AuthWrapper, LogoWrapper } from './AuthStyles';
import logo from '/ui/new-spellcaster-logo.svg';

export default function AdminResetPassword() {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);

  const [search] = useSearchParams();
  const code = search.get('oobCode');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<ReactNode | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!code) navigate(getDefaultDashboardRoute());
  }, [code, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccess(false);
    setError('');

    if (!code) {
      setError('Invalid code. Please check the URL and try again.');
      return;
    }

    if (password !== passwordConfirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(api.asEducator.auth, code, password);
      setLoading(false);
      setSuccess(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      setLoading(false);
      const code =
        error && typeof error === 'object' && 'code' in error
          ? error.code
          : 'unknown';
      if (code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (code === 'auth/invalid-action-code') {
        setError(
          'This reset link is invalid or has expired. Please request a new one.',
        );
      } else {
        setError(UNKNOWN_ERROR_MESSAGE);
      }
    }
  }

  return (
    <AuthViewWrapper>
      <LogoWrapper onClick={() => navigate('/')}>
        <img src={logo} alt="logo" />
      </LogoWrapper>
      <AuthWrapper onSubmit={handleSubmit}>
        {success ? (
          <>
            <div className="flex flex-col gap-2 items-center text-sm text-muted-foreground text-center">
              <CheckCircleIcon size={40} className="text-green-600 mb-2" />
              <p>All done! You can now log in with your new password.</p>
            </div>
            <Button type="button" className="w-full mt-2" onClick={() => navigate('/login')}>
              Log in
            </Button>
          </>
        ) : (
          <>
            {error && (
              <Alert variant="destructive">
                <AlertCircleIcon size={16} className="ml-2" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <p className="text-sm text-muted-foreground text-center">
              Choose a new password for your account.
            </p>

            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New Password"
              disabled={isLoading}
            />
            <Input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="Confirm New Password"
              disabled={isLoading}
            />
            <Button type="submit" loading={isLoading} className="w-full mt-2">
              Reset Password
            </Button>
            <Button
              type="button"
              variant="link"
              disabled={isLoading}
              className="p-0 h-fit"
              onClick={() => navigate('/login')}
            >
              Back to log in
            </Button>
          </>
        )}
      </AuthWrapper>
    </AuthViewWrapper>
  );
}
